import {
  DEFAULT_INVALIDATE_OPTIONS,
  DEFAULT_QUERY_OPTIONS,
  UseMutationCallbackType,
} from "#hooks/adapters/adapterUtils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PillProps, useToast } from "@validereinc/common-components";
import {
  AssetGroupAdapter,
  AssetGroupStatus,
  AssetGroupStatusType,
  AssetGroupType,
  NetworkAdapter,
} from "@validereinc/domain";

export const NETWORKS_QUERY_KEY = "networks";
export const NetworkStatusPillVariants: Record<
  AssetGroupStatusType,
  PillProps["variant"]
> = {
  [AssetGroupStatus.ACTIVE]: "success",
  [AssetGroupStatus.INACTIVE]: "default",
} as const;

export const useGetOneNetwork = (
  networkId?: string,
  meta: { period?: string } = {}
) =>
  useQuery({
    queryKey: [NETWORKS_QUERY_KEY, "getOne", networkId, meta] as const,
    queryFn: ({ queryKey: [_, __, id, meta] }) =>
      NetworkAdapter.getOne({ id: id ?? "", meta }),
    select: (resp) => resp.data,
    enabled: !!networkId,
    ...DEFAULT_QUERY_OPTIONS,
  });

export const useListNetworks = (
  apiParams: Parameters<typeof AssetGroupAdapter.getList>[0] = {},
  options: {
    select?: any;
    enabled?: boolean;
  } = {}
) =>
  useQuery({
    queryKey: [
      NETWORKS_QUERY_KEY,
      "getList",
      { ...apiParams, type: { $exact: "network" } },
    ] as const,
    queryFn: ({ queryKey: [_, __, params] }) =>
      AssetGroupAdapter.getList(params),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });

export const useCreateNetwork = ({
  onSuccess,
}: UseMutationCallbackType<AssetGroupType> = {}) => {
  const { toast } = useToast();
  const { invalidate } = useClearNetworksCache();

  return useMutation({
    mutationFn: async (
      params: Parameters<typeof NetworkAdapter.createOne>[0]
    ) => NetworkAdapter.createOne(params),
    onSuccess: (data, variables, context) => {
      toast.push({
        intent: "success",
        description: `Successfully created network`,
      });
      invalidate();
      onSuccess?.(data.data, variables, context);
    },
    onError: () => {
      toast.push({
        intent: "error",
        description: "Failed to create network",
      });
    },
  });
};

export const useUpdateNetwork = ({
  onSuccess,
}: UseMutationCallbackType<AssetGroupType> = {}) => {
  const { toast } = useToast();
  const { invalidate } = useClearNetworksCache();

  return useMutation({
    mutationFn: async (
      updateParams: Parameters<typeof NetworkAdapter.updateOne>[0]
    ): Promise<Awaited<ReturnType<typeof NetworkAdapter.updateOne>>> => {
      const { flow_ids: flowIds, ...networkData } = updateParams.data;
      const requests: Array<Promise<unknown>> = [];

      const updateNetworkRequest =
        Object.keys(networkData).length > 0
          ? NetworkAdapter.updateOne({
              id: updateParams.id,
              data: networkData,
            })
          : null;

      if (updateNetworkRequest) {
        requests.push(updateNetworkRequest);
      }

      if (flowIds !== undefined) {
        requests.push(
          AssetGroupAdapter.assets.updateMany({
            ids: flowIds,
            data: {},
            meta: {
              assetGroupId: updateParams.id,
            },
          })
        );
      }

      if (!requests.length) {
        return {
          data: {
            id: updateParams.id,
          } as AssetGroupType,
        };
      }

      await Promise.all(requests);

      return (
        updateNetworkRequest ?? {
          data: {
            id: updateParams.id,
          } as AssetGroupType,
        }
      );
    },
    onSuccess: async (data, variables, context) => {
      toast.push({
        intent: "success",
        description: `Successfully updated network`,
      });
      invalidate();
      onSuccess?.(data.data, variables, context);
    },
    onError: () => {
      toast.push({
        intent: "error",
        description: "Failed to update network",
      });
    },
  });
};

export const useDeleteNetwork = ({
  onSuccess,
}: UseMutationCallbackType<
  Awaited<ReturnType<typeof NetworkAdapter.deleteOne>>
> = {}) => {
  const { toast } = useToast();
  const { invalidate } = useClearNetworksCache();

  return useMutation({
    mutationFn: (networkId: string) =>
      NetworkAdapter.deleteOne({
        id: networkId,
      }),
    onSuccess: (data, variables, context) => {
      toast.push({
        intent: "success",
        description: `Network has been successfully deleted.`,
      });
      invalidate();
      onSuccess?.(data, variables, context);
    },
  });
};

export const useMigrateNetwork = () => {
  const { toast } = useToast();
  const { invalidate } = useClearNetworksCache();

  return useMutation({
    mutationFn: (networkId: string) =>
      AssetGroupAdapter.migrate({
        id: networkId,
        data: {},
      }),
    onSuccess: async () => {
      toast.push({
        intent: "success",
        description: `Network has been successfully migrated.`,
      });
      invalidate();
    },
    onError: () => {
      toast.push({
        intent: "error",
        description: `Network migration failed.`,
      });
    },
  });
};

export const useClearNetworksCache = () => {
  const queryClient = useQueryClient();
  return {
    invalidate: () => {
      queryClient.invalidateQueries({
        queryKey: [NETWORKS_QUERY_KEY],
        ...DEFAULT_INVALIDATE_OPTIONS,
      });
    },
  };
};
